import React from 'react';

import {
  Dimensions,
  Modal,
  ModalProps,
  ScrollView,
  StatusBar,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import ClickableBackground from '../ClickableBackground';
import Shadow from '../Shadow';

const SCREEN_INDENT = 8;
export interface PopUpProps extends ModalProps {
  /**
   * The anchor to open the menu from. In most cases, it will be a button that opens the menu.
   */
  anchor: React.ReactNode;

  /**
   * Optional callback called on hide pop up.
   */
  onHidden?(): void;

  /**
   * Place popup on top of anchor or below, default is false.
   */
  overlay?: boolean;

  /**
   * Optional x-axis offset
   */
  offsetX?: number;

  /**
   * Optional y-axis offset
   */
  offsetY?: number;

  /**
   * One of the different types of text presets.
   */
  // preset?: ButtonPresetNames

  /**
   * A button can support use of custom children, other that text. Useful for the Calendar header
   */
  children?: React.ReactNode;

  /**
   * An optional style override useful for padding & margin.
   */
  style?: ViewStyle | ViewStyle[];
}

export interface PopUpState {
  shown: boolean;
  top: number;
  left: number;
  menuWidth: number;
  menuHeight: number;
  buttonWidth: number;
  buttonHeight: number;
}

const MENU_CONTAINER: ViewStyle = {};

const SHADOW_MENU_CONTAINER: ViewStyle = {
  position: 'absolute',
  backgroundColor: 'white',
  borderRadius: 4,
  opacity: 0,
};

const supportedOrientations: any = [
  'portrait',
  'portrait-upside-down',
  'landscape',
  'landscape-left',
  'landscape-right',
];

export class PopUp extends React.Component<PopUpProps, PopUpState, any> {
  state = {
    shown: false,
    top: 0,
    left: 0,
    menuWidth: 0,
    menuHeight: 0,
    buttonWidth: 0,
    buttonHeight: 0,
  };

  container: View | null = null;

  setContainerRef = (ref: View): void => {
    this.container = ref;
  };

  // Start menu animation
  onMenuLayout = (e) => {
    const { width, height } = e.nativeEvent.layout;
    this.setState({
      shown: true,
      menuWidth: width,
      menuHeight: height,
    });
  };

  show = (): void => {
    this.container?.measureInWindow(
      (
        left: number,
        top: number,
        buttonWidth: number,
        buttonHeight: number
      ): void => {
        this.setState({
          buttonHeight,
          buttonWidth,
          left,
          shown: true,
          top,
        });
      }
    );
  };

  hide = (onHidden?: () => void): void => {
    this.setState({
      shown: false,
    });
    // Invoke onHidden callback if defined
    if (onHidden) {
      onHidden();
    }
  };

  render(): JSX.Element {
    const dimensions = Dimensions.get('window');
    const { width: windowWidth } = dimensions;
    const windowHeight = dimensions.height - (StatusBar.currentHeight || 0);

    const { menuWidth, menuHeight, buttonWidth, buttonHeight, shown } =
      this.state;

    const {
      anchor,
      style,
      children,
      onHidden,
      offsetX = 0,
      offsetY = 0,
      overlay = false,
      ...rest
    } = this.props;

    // Adjust position of menu
    let { left, top } = this.state;
    left = left + offsetX;
    top = top + offsetY;

    if (left + menuWidth > windowWidth - SCREEN_INDENT) {
      left = windowWidth - SCREEN_INDENT - menuWidth;
    } else if (left < SCREEN_INDENT) {
      left = SCREEN_INDENT;
    }

    if (!overlay) {
      top = top + buttonHeight;
    }

    // use the maximum allowable top value if menu does not fit in screen
    if (top > windowHeight - menuHeight - SCREEN_INDENT) {
      top = windowHeight - menuHeight - SCREEN_INDENT;
    } else if (top < SCREEN_INDENT) {
      top = SCREEN_INDENT;
    } else if (top + menuHeight > windowHeight) {
      top = windowHeight - menuHeight - SCREEN_INDENT;
    }

    const shadowMenuContainerStyle = {
      opacity: 1,
      top,
      left,
    };

    return (
      <View ref={this.setContainerRef} collapsable={false}>
        {anchor}

        <Modal
          {...rest}
          visible={shown}
          onRequestClose={() => this.hide(onHidden)}
          supportedOrientations={supportedOrientations}
          transparent
        >
          <View style={StyleSheet.absoluteFill}>
            <ClickableBackground onPress={() => this.hide(onHidden)} />
            <Shadow
              onLayout={this.onMenuLayout}
              style={[SHADOW_MENU_CONTAINER, shadowMenuContainerStyle, style]}
            >
              <ScrollView
                style={[MENU_CONTAINER, { maxHeight: windowHeight / 2 }]}
              >
                {children}
              </ScrollView>
            </Shadow>
          </View>
        </Modal>
      </View>
    );
  }
}
